import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trash2, ChevronDown, ChevronUp, Briefcase, FileDown } from 'lucide-react';
import { getBackendUrl } from '../../lib/utils';

const AdminHR = () => {
  const [applications, setApplications] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const url = getBackendUrl();
      const { data } = await axios.get(`${url}/api/hr-applications`, {
        withCredentials: true
      });
      setApplications(data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleExpand = (id) => {
    const next = new Set(expandedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpandedIds(next);
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation(); // Prevent toggling expand/collapse
    if (!window.confirm("Bu başvuruyu silmek istediğinize emin misiniz?")) return;
    try {
      const url = getBackendUrl();
      await axios.delete(`${url}/api/hr-applications/${id}`, { withCredentials: true });
      fetchApplications();
    } catch (e) {
      alert("Hata oluştu, silinemedi.");
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(applications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedApps = applications.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="space-y-6">
      {applications.length === 0 ? (
        <div className="bg-white p-12 rounded-3xl border border-slate-200/80 text-center text-slate-500 flex flex-col items-center justify-center gap-3">
          <Briefcase size={40} className="text-slate-300" />
          <span className="font-bold text-sm">Henüz başvuru bulunmuyor.</span>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-3xl shadow-sm border border-slate-200/80 overflow-hidden divide-y divide-slate-100">
            {displayedApps.map(app => {
              const appId = app.id || app._id;
              const isExpanded = expandedIds.has(appId);
              const cvUrl = app.cv_url.startsWith('http') ? app.cv_url : `${getBackendUrl()}${app.cv_url}`;
              
              return (
                <div key={appId} className="transition-all duration-200 hover:bg-slate-50/50">
                  {/* Clickable Header */}
                  <div 
                    onClick={() => toggleExpand(appId)} 
                    className="p-6 flex items-center justify-between gap-4 cursor-pointer select-none"
                  >
                    <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Name & Phone */}
                      <div className="min-w-0">
                        <div className="font-bold text-slate-900 text-sm truncate">{app.name}</div>
                        <div className="text-xs text-slate-400 truncate">{app.phone}</div>
                      </div>
                      
                      {/* Department */}
                      <div className="min-w-0 flex items-center">
                        <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-wider">
                          {app.department || 'Genel Başvuru'}
                        </span>
                      </div>

                      {/* CV Link */}
                      <div className="min-w-0 flex items-center">
                        <a 
                          href={cvUrl} 
                          target="_blank" 
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()} // Stop triggering expand/collapse
                          className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-bold text-xs bg-blue-50 hover:bg-blue-100/80 px-3 py-1.5 rounded-lg transition-all"
                        >
                          <FileDown size={14} /> Özgeçmiş (CV)
                        </a>
                      </div>
                    </div>

                    {/* Right action & meta */}
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="text-xs font-bold text-slate-400">
                        {new Date(app.date_created).toLocaleDateString('tr-TR')}
                      </span>
                      
                      <button 
                        onClick={(e) => handleDelete(e, appId)}
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-2 rounded-xl transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                      
                      <span className="text-slate-400">
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Body */}
                  {isExpanded && (
                    <div className="px-6 pb-6 pt-2 bg-slate-50/50 border-t border-slate-100 space-y-4">
                      <div>
                        <div className="text-xs font-bold text-slate-400 mb-1">E-POSTA ADRESİ:</div>
                        <a 
                          href={`mailto:${app.email}`} 
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm font-bold text-blue-600 hover:underline"
                        >
                          {app.email}
                        </a>
                      </div>
                      
                      <div>
                        <div className="text-xs font-bold text-slate-400 mb-2">ÖN YAZI (COVER LETTER):</div>
                        <p className="text-slate-700 whitespace-pre-line text-sm leading-relaxed bg-white border border-slate-200/80 p-5 rounded-2xl">
                          {app.cover_letter || 'Ön yazı belirtilmemiş.'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold bg-white text-slate-600 hover:bg-slate-50 transition disabled:opacity-50"
              >
                Önceki
              </button>
              
              <div className="text-xs font-bold text-slate-500">
                {currentPage} / {totalPages}
              </div>

              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold bg-white text-slate-600 hover:bg-slate-50 transition disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminHR;
